import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AppBarContext from '../../../components/BusinessPortalAppBarContext';
import { isElementId } from '../../../utils/url-utils';
import { useDashboard } from '../../Dashboard/DashboardContext';
import KPIInputForm from './KPIInputForm';
import KPIList from './KPIList';
import { Comment, EditableComment, EditableSeries, Measure, Series } from './types';

interface KPITableViewProps {
  title?: string;
  series?: Series[];
  comments?: Comment;
}

interface Payload {
  elementId: string;
  value: string;
  period: string;
  series: string;
}

const KPITableView = ({
  title = '',
  series = [],
  comments = { enabled: false }
}: KPITableViewProps) => {
  const { setError } = useErrorContext();
  const queryClient = useQueryClient();

  const [queryString] = useDashboard();
  const { getPeriodName, selectedPeriod } = useContext(AppBarContext);

  const [selected, setSelected] = React.useState<Measure | null>(null);

  const handleSelect = (newSelected: Measure | null) => {
    setSelected(selected === newSelected ? null : newSelected);
    setError(null);
  };

  const meaId = queryString.get('meaId') || '';

  const seriesToQuery = series.map(
    serie =>
      `prettyvalue(series="${serie.name}", period=${getPeriodName()}, as="${
        serie.displayName || serie.name
      }"), inputtypevalue(as="it_${serie.name}",series="${serie.name}"),unitname(as="un_${
        serie.name
      }",series="${serie.name}")`
  );

  const commentQuery = comments.enabled
    ? `, value(series="${
        comments.commentSeries
      }", period=${getPeriodName()}, as="comment"), inputtypevalue(as="it_${
        comments.commentSeries
      }",series="${comments.commentSeries}")`
    : '';

  const { isFetching, isLoading, error, data, isFetched } = useQuery<
    AxiosResponse<any[]>,
    AxiosError<ErrorObject>
  >(
    `KPITable-${meaId || ''}-${getPeriodName()}`,
    async () => {
      const response = await axios.post('measurehierarchy', {
        parentId: meaId,
        attributes: `name, ${seriesToQuery.join(',')}, periodlevelid${commentQuery}`
      });
      return response.data;
    },
    {
      // The query will not execute until the meaId exists, it is valid elementId and starts with SC
      enabled: !!selectedPeriod && !!meaId && isElementId(meaId) && meaId.startsWith('SC')
    }
  );

  if (error && error.response) setError(error.response.data);

  const mutation = useMutation(
    async (measureToUpdate: any) => {
      const payload: Payload[] = [];

      Object.entries(measureToUpdate.values).forEach(entry => {
        const [key, value] = entry;

        if (typeof value === 'string')
          payload.push({
            elementId: measureToUpdate.elementId,
            value,
            period: measureToUpdate.period,
            series: key
          });
      });

      const response = await axios.post('measurevalues', payload);
      return response.data;
    },
    {
      onError: (errorResponse: AxiosError<ErrorObject>, variables) => {
        if (errorResponse.response) setError(errorResponse?.response?.data);
        console.error(`there was an error updating values: ${JSON.stringify(variables)}`);
        queryClient.invalidateQueries(`KPITable-${meaId || ''}-${getPeriodName()}`);
      },
      onSuccess: () => {
        setError(null);
        queryClient.invalidateQueries(`KPITable-${meaId || ''}-${getPeriodName()}`);
      }
    }
  );

  const handleEdit = (inputValues: EditableSeries[], comment: EditableComment) => {
    const values: any = {};

    inputValues.forEach((value: EditableSeries) => {
      if (value.inputType === '0' && value.changed === true && value.name)
        values[value.name] = value.value;
    });

    if (comment.changed && comment.commentSeries) values[comment.commentSeries] = comment.value;

    mutation.mutate({ elementId: selected?.id, values, period: getPeriodName() });
  };

  const parseDataForKPIForm = () => {
    const parsedData: EditableSeries[] = [];

    if (!selected) return [];

    const found = data?.data[0].children.find((measure: any) => measure.id === selected.id);

    if (!found) return [];

    series.forEach((serie: Series) => {
      if (serie.editable)
        parsedData.push({
          ...serie,
          inputType: found[`it_${serie.name}`],
          changed: false,
          value: found[`${serie.displayName || serie.name}`],
          unitName: found[`un_${serie.name}`]
        });
    });
    return parsedData;
  };

  const formData: EditableSeries[] = parseDataForKPIForm();

  const getCommentsData = (): EditableComment | undefined => {
    if (!selected || !comments.enabled) return;

    const found = data?.data[0].children.find((measure: any) => measure.id === selected.id);

    if (!found) return;

    // eslint-disable-next-line consistent-return
    return {
      ...comments,
      value: found.comment || '',
      inputType: found[`it_${comments.commentSeries}`],
      changed: false
    };
  };

  const commentsData = getCommentsData();

  if (!isLoading && !isFetched) return null;

  if (mutation.isLoading || isFetching || isLoading)
    return (
      <div data-testid="loadingIndicator" style={{ width: '40px', margin: 'auto' }}>
        <CircularProgress />
      </div>
    );

  return data ? (
    <>
      <Typography>{title}</Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <KPIList
            data={data?.data[0] && data?.data[0].children}
            series={series}
            handleSelect={handleSelect}
            selected={selected}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card style={{ minHeight: '400px' }}>
            {(() => {
              if (!selected)
                return (
                  <Typography data-testid="notSelectedWarning">Select KPI from the list</Typography>
                );
              if (!selectedPeriod || selectedPeriod.periodlevel !== selected.periodlevelid)
                return (
                  <Typography data-testid="periodLevelWarning">
                    Selected period level is not supported by the measure. Please try choosing an
                    another period level
                  </Typography>
                );
              return (
                <KPIInputForm
                  formData={formData}
                  comments={commentsData}
                  handleEdit={handleEdit}
                  title={selected.name}
                />
              );
            })()}
          </Card>
        </Grid>
      </Grid>
    </>
  ) : (
    <div data-testid="KPIDescriptionCard-no-data">No data found</div>
  );
};

export default KPITableView;
